import { App } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { formatFilterParams } from 'business/quality-control/pages/segment-history/hooks/data/utils';
import { useSegmentHistoryFilters } from 'business/quality-control/pages/segment-history/providers/filters';
import { useSegmentHistoryV1ControllerBulkUpdateStatus } from 'generated/apiComponents';
import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';

interface BulkStatusUpdateValues {
  newStatus: Quality_SegmentHistoryStatusEnum_Enum;
  scanSiteId: string;
  includedSegmentIds?: string[];
}

export const useBulkStatusUpdate = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const filters = useSegmentHistoryFilters();

  const { mutate, isPending } = useSegmentHistoryV1ControllerBulkUpdateStatus();

  const submit = (values: BulkStatusUpdateValues) => {
    mutate(
      {
        pathParams: { constructionSiteId: currentConstructionSite.id },
        body: {
          ...formatFilterParams(filters),
          ...values,
        },
      },
      {
        onSuccess: (result) => {
          message.open({
            type: 'success',
            content: t(
              'qualityControl.segmentHistory.bulkStatusModal.success',
              { count: result.count },
            ),
          });
          if (result.quarantineCount > 0) {
            message.open({
              type: 'warning',
              content: t(
                'qualityControl.segmentHistory.bulkStatusModal.quarantineWarning',
                { count: result.quarantineCount },
              ),
            });
          }
          onSuccess();
        },
        onError: () => {
          message.open({
            type: 'error',
            content: t(
              'qualityControl.segmentHistory.bulkStatusModal.errors.technicalError',
            ),
          });
        },
      },
    );
  };

  return { submit, isPending };
};
